import { API_BASE_URL } from '../config/serverApiConfig';
import { api } from './api';

/**
 * The platform console API (`/platform/*`): directory metadata and counts, never customer
 * security records. Every read here is open to both platform roles; the writes
 * (`createOrganisation`, the support-admin calls and the invitation actions) are answered
 * 403 for anyone but the PLATFORM_OWNER, so the console renders them for the owner only.
 *
 * Reads go uncached: `api()` installs axios-cache-interceptor, and each of these lists is
 * re-read straight after a write, where a cached copy would show the operator their own
 * change being undone.
 */
const base = `${API_BASE_URL}platform`;
const fresh = { cache: false } as any;

export type PlatformRole = 'PLATFORM_OWNER' | 'SUPPORT_ADMIN';

export interface PlatformOrganisation {
  id: number;
  name: string;
  slug: string;
  created_at: string;
  status: 'Active' | 'Suspended' | 'Deleted';
  is_sandbox: boolean;
  suspended_at: string | null;
  head_email: string | null;
  workspace_count: number;
  member_count: number;
  asset_count: number;
  vulnerability_count: number;
}

export interface PlatformEntitySummary {
  id: number;
  name: string;
  created_at: string;
  workspace_count: number;
  member_count: number;
  asset_count: number;
}

export interface PlatformOrganisationDetail extends PlatformOrganisation {
  entities: PlatformEntitySummary[];
}

/** What creating an organisation did: the head is invited, not placed. */
export interface PlatformHeadReplaced {
  organisation_id: number;
  previous_head_email: string | null;
  invitation: PlatformOrganisationCreated['invitation'];
}

export interface PlatformOrganisationCreated extends PlatformOrganisation {
  invitation: {
    email: string;
    status: string;
    delivery: 'sent' | 'failed' | null;
  };
}

export interface PlatformMembership {
  organisation_id: number;
  organisation_name: string;
  entity_id?: number | null;
  entity_name?: string | null;
  node_kind: 'organisation' | 'entity' | 'workspace';
  role: string;
}

export interface PlatformUser {
  id: string;
  name: string;
  email: string;
  status: 'Active' | 'Disabled';
  platform_role: PlatformRole | null;
  last_login_at?: string | null;
  last_seen_at?: string | null;
  memberships: PlatformMembership[];
  can_be_support_admin: boolean;
}

export interface PlatformUserList {
  items: PlatformUser[];
  total: number;
}

export interface PlatformOverview {
  organisation_count: number;
  workspace_count: number;
  user_count: number;
  pending_invitation_count: number;
  support_admin_count: number;
  recent_organisations: PlatformOrganisation[];
  recent_users: PlatformUser[];
}

export interface PlatformInvitationGrant {
  node_id: number;
  node_name: string;
  role: string;
}

export type PlatformInvitationTarget =
  | { type: 'platform'; role: 'SUPPORT_ADMIN' }
  | {
      type: 'organisation';
      organisation_id: number;
      organisation_name: string | null;
      is_head?: boolean;
      grants: PlatformInvitationGrant[];
    };

export interface PlatformInvitation {
  id: number;
  email: string;
  name: string | null;
  status: string;
  expires_at: string;
  created_at: string;
  target: PlatformInvitationTarget;
}

/**
 * What inviting, or resending, to SUPPORT_ADMIN did: `granted` (the address already had an
 * account and now holds the role), `invitation_pending` (a link was issued; `delivery` says
 * whether the mail went) or `existing_account` (a resend found an account, so nothing was
 * mailed and nothing was granted).
 */
export interface PlatformInvitationIssued {
  status: 'granted' | 'invitation_pending' | 'existing_account';
  user_id?: string | null;
  invitation_id?: number | null;
  delivery?: 'sent' | 'failed' | null;
  expires_at?: string | null;
}

export interface CreateOrganisationBody {
  name: string;
  slug?: string;
  head_email: string;
  head_name?: string;
}

export interface PlatformAuditRow {
  id: number;
  workspace_id: number | null;
  actor_id: string | null;
  target_user_id: string | null;
  scope_type: string;
  event: string;
  subject: string | null;
  detail: Record<string, unknown>;
  at: string;
}

export const platformApi = {
  overview: (): Promise<PlatformOverview> =>
    api()
      .get(`${base}/overview`, fresh)
      .then((r) => r.data),
  organisations: (search = ''): Promise<PlatformOrganisation[]> =>
    api()
      .get(`${base}/organisations`, {
        params: { search: search || undefined },
        ...fresh
      })
      .then((r) => r.data),
  organisation: (id: number | string): Promise<PlatformOrganisationDetail> =>
    api()
      .get(`${base}/organisations/${id}`, fresh)
      .then((r) => r.data),
  createOrganisation: (data: CreateOrganisationBody): Promise<PlatformOrganisationCreated> =>
    api()
      .post(`${base}/organisations`, data)
      .then((r) => r.data),
  replaceHead: (id: number, data: { email: string; name?: string }): Promise<PlatformHeadReplaced> =>
    api()
      .post(`${base}/organisations/${id}/head`, data)
      .then((r) => r.data),
  suspendOrganisation: (id: number) =>
    api()
      .post(`${base}/organisations/${id}/suspend`)
      .then((r) => r.data),
  resumeOrganisation: (id: number) =>
    api()
      .post(`${base}/organisations/${id}/resume`)
      .then((r) => r.data),
  users: (
    params: {
      search?: string;
      limit?: number;
      offset?: number;
      platform_role?: PlatformRole | 'NONE';
      organisation_id?: number;
      status?: 'active' | 'disabled';
      scope?: 'default' | 'all';
    } = {}
  ): Promise<PlatformUserList> =>
    api()
      .get(`${base}/users`, {
        params: { ...params, search: params.search || undefined },
        ...fresh
      })
      .then((r) => r.data),
  assignSupportAdmin: (userId: string) =>
    api()
      .put(`${base}/support-admins/${userId}`)
      .then((r) => r.data),
  removeSupportAdmin: (userId: string) =>
    api()
      .delete(`${base}/support-admins/${userId}`)
      .then((r) => r.data),
  inviteSupportAdmin: (body: { email: string; name?: string }): Promise<PlatformInvitationIssued> =>
    api()
      .post(`${base}/support-admins/invitations`, body)
      .then((r) => r.data),
  invitations: (): Promise<PlatformInvitation[]> =>
    api()
      .get(`${base}/invitations`, fresh)
      .then((r) => r.data),
  resendInvitation: (id: number): Promise<PlatformInvitationIssued> =>
    api()
      .post(`${base}/invitations/${id}/resend`)
      .then((r) => r.data),
  revokeInvitation: (id: number) =>
    api()
      .post(`${base}/invitations/${id}/revoke`)
      .then((r) => r.data),
  audit: (params: Record<string, unknown> = {}): Promise<{ items: PlatformAuditRow[]; total: number }> =>
    api()
      .get(`${base}/audit`, { params, ...fresh })
      .then((r) => r.data)
};

/**
 * The message to put in front of the operator for a failed platform call: the server's own
 * `detail` when it is a sentence, the joined `msg`s of a 422, else `fallback`.
 */
export const platformError = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const joined = detail
      .map((entry: any) => entry?.msg)
      .filter((text: unknown): text is string => typeof text === 'string' && text.trim() !== '')
      .join(' ');
    if (joined) return joined;
  }
  return fallback;
};
